import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Download, Eye, Trash2, PackageCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID");
const idr = (n) => `Rp${num(n)}`;

export default function CustomerReturns() {
  const { can } = useAuth();
  const canWrite = can("sales");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [condition, setCondition] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);

  const params = useMemo(() => ({ q: debounced || undefined, condition: condition !== "all" ? condition : undefined, start: start || undefined, end: end || undefined, page, limit }), [debounced, condition, start, end, page, limit]);
  const load = () => { setLoading(true); api.get("/customer-returns", { params }).then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat")).finally(() => setLoading(false)); };
  useEffect(load, [params]);

  const doExport = () => downloadFile("/customer-returns/export", { q: params.q, condition: params.condition, start: params.start, end: params.end }, "return_customer.xlsx").catch(() => toast.error("Gagal export"));
  const doDelete = async (row) => {
    if (!window.confirm(`Batalkan Return ${row.number}? Stok yang dikembalikan akan ditarik lagi & refund dibatalkan.`)) return;
    try { await api.delete(`/customer-returns/${row.id}`); toast.success("Return dibatalkan"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const start0 = (data.page - 1) * limit;
  const condBadges = (items) => {
    const layak = items.filter((i) => i.condition === "layak").reduce((s, i) => s + i.qty, 0);
    const rusak = items.filter((i) => i.condition === "rusak").reduce((s, i) => s + i.qty, 0);
    return (
      <div className="flex gap-1 justify-center">
        {layak > 0 && <Badge variant="outline" className="border-green-300 text-green-700">Layak {layak}</Badge>}
        {rusak > 0 && <Badge variant="outline" className="border-red-300 text-red-600">Rusak {rusak}</Badge>}
      </div>
    );
  };

  return (
    <div data-testid="customer-returns-page">
      <PageHeader title="Return Customer" subtitle="Pengembalian barang dari penjualan (marketplace / nota)">
        {canWrite && <Button onClick={() => setFormOpen(true)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="ret-create-btn"><Plus className="w-4 h-4 mr-1.5" />Create</Button>}
        <Button variant="outline" onClick={doExport} data-testid="ret-export-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>
      </PageHeader>

      <Panel className="p-0">
        <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-taupe/15">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari No Return / No Sales / Order ID / Customer..." className="pl-8 h-9" data-testid="ret-search" />
          </div>
          <div><Label className="text-xs">Kondisi</Label>
            <Select value={condition} onValueChange={(v) => { setCondition(v); setPage(1); }}>
              <SelectTrigger className="h-9 w-36" data-testid="ret-filter-condition"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="layak">Layak</SelectItem><SelectItem value="rusak">Rusak</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPage(1); }} className="h-9 w-40" data-testid="ret-filter-start" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPage(1); }} className="h-9 w-40" data-testid="ret-filter-end" /></div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">No</TableHead>
              <TableHead>No. Return</TableHead>
              <TableHead>No. Sales</TableHead>
              <TableHead>Order ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="text-center">Qty</TableHead>
              <TableHead className="text-center">Kondisi</TableHead>
              <TableHead className="text-right">Nilai / Refund</TableHead>
              <TableHead>Tanggal</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-wmuted">Memuat...</TableCell></TableRow>
            ) : data.items.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-wmuted">Belum ada data return</TableCell></TableRow>
            ) : data.items.map((r, i) => (
              <TableRow key={r.id} data-testid={`ret-row-${r.id}`}>
                <TableCell className="text-wmuted">{start0 + i + 1}</TableCell>
                <TableCell className="font-medium text-cocoa">{r.number}</TableCell>
                <TableCell>{r.sale_number}</TableCell>
                <TableCell>{r.order_id || "-"}</TableCell>
                <TableCell>{r.customer_name || "-"}</TableCell>
                <TableCell className="text-center">{num(r.total_qty)}</TableCell>
                <TableCell>{condBadges(r.items || [])}</TableCell>
                <TableCell className="text-right">{idr(r.returned_value)} <span className="text-wmuted">/ {idr(r.refund_amount)}</span></TableCell>
                <TableCell className="text-sm">{(r.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetail(r)} data-testid={`ret-detail-${r.id}`}><Eye className="w-4 h-4" /></Button>
                  {canWrite && <Button size="icon" variant="ghost" className="h-8 w-8 text-status-reject" onClick={() => doDelete(r)} data-testid={`ret-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-taupe/15 text-sm">
          <div className="flex items-center gap-2 text-wmuted">
            <span>Show</span>
            <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-8 w-20" data-testid="ret-limit"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <span>entries · Menampilkan {data.total === 0 ? 0 : start0 + 1}–{Math.min(start0 + limit, data.total)} dari {data.total}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="ret-prev">Previous</Button>
            <span className="px-3 text-wmuted">Hal {data.page} / {data.pages}</span>
            <Button size="sm" variant="outline" disabled={data.page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="ret-next">Next</Button>
          </div>
        </div>
      </Panel>

      {formOpen && <ReturnForm onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
      {detail && <DetailDialog row={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ReturnForm({ onClose, onSaved }) {
  const [sq, setSq] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sale, setSale] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [lines, setLines] = useState({}); // sale_item_id -> {qty, condition, warehouse_id}
  const [refundMethod, setRefundMethod] = useState("Cash");
  const [refundAmount, setRefundAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/warehouses", { params: { usage: "produk_jadi" } }).then((r) => setWarehouses(r.data || [])).catch(() => {}); }, []);

  const doSearch = () => {
    if (!sq || sq.length < 2) return toast.info("Ketik minimal 2 karakter");
    setSearching(true);
    api.get("/customer-returns/sale-lookup", { params: { q: sq } }).then((r) => {
      setResults(r.data.sales || []);
      if ((r.data.sales || []).length === 0) toast.info("Nota tidak ditemukan");
    }).catch(() => toast.error("Gagal mencari")).finally(() => setSearching(false));
  };

  const selectSale = (s) => {
    api.get(`/customer-returns/sale/${s.id}`).then((r) => {
      setSale(r.data);
      setResults([]);
      const defWh = warehouses[0]?.id || "";
      const init = {};
      (r.data.items || []).forEach((it) => { init[it.sale_item_id] = { qty: 0, condition: "layak", warehouse_id: it.warehouse_id || defWh }; });
      setLines(init);
    }).catch(() => toast.error("Gagal memuat nota"));
  };

  const setLine = (id, patch) => setLines((l) => ({ ...l, [id]: { ...l[id], ...patch } }));

  const returnedValue = useMemo(() => {
    if (!sale) return 0;
    return (sale.items || []).reduce((s, it) => s + (Number(lines[it.sale_item_id]?.qty || 0) * it.price), 0);
  }, [sale, lines]);

  useEffect(() => { setRefundAmount(String(returnedValue)); }, [returnedValue]);

  const save = async () => {
    const items = (sale?.items || [])
      .filter((it) => Number(lines[it.sale_item_id]?.qty || 0) > 0)
      .map((it) => ({ sale_item_id: it.sale_item_id, qty: Number(lines[it.sale_item_id].qty), condition: lines[it.sale_item_id].condition, warehouse_id: lines[it.sale_item_id].warehouse_id }));
    if (items.length === 0) return toast.error("Isi qty return minimal 1 item");
    if (items.some((i) => !i.warehouse_id)) return toast.error("Pilih gudang tujuan untuk setiap item yang direturn");
    setSaving(true);
    try {
      await api.post("/customer-returns", { sale_id: sale.id, items, refund_amount: Number(refundAmount || 0), refund_method: refundMethod, note });
      toast.success("Return berhasil dicatat");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ret-form-dialog">
        <DialogHeader>
          <DialogTitle>Create Return Customer</DialogTitle>
          <DialogDescription>Cari nota berdasarkan No. Sales atau Order ID Marketplace, lalu pilih barang yang direturn.</DialogDescription>
        </DialogHeader>

        {!sale ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={sq} onChange={(e) => setSq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="No. Sales (mis. SALES0003) atau Order ID..." data-testid="ret-sale-search" autoFocus />
              <Button onClick={doSearch} disabled={searching} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="ret-sale-search-btn">{searching ? "..." : "Cari"}</Button>
            </div>
            <div className="border border-taupe/20 rounded-lg divide-y divide-taupe/10 max-h-72 overflow-y-auto">
              {results.length === 0 ? <div className="px-3 py-6 text-center text-wmuted text-sm">Belum ada hasil. Cari nota di atas.</div> :
                results.map((s) => (
                  <button key={s.id} type="button" onClick={() => selectSale(s)} data-testid={`ret-sale-pick-${s.id}`}
                    className="w-full text-left px-3 py-2.5 hover:bg-taupe/10 flex justify-between items-center">
                    <div>
                      <div className="font-medium text-cocoa text-sm">{s.number} {s.order_id ? <span className="text-wmuted">· Order {s.order_id}</span> : null}</div>
                      <div className="text-xs text-wmuted">{s.customer_name || "-"} · {(s.created_at || "").slice(0, 10)} {s.marketplace ? `· ${s.marketplace}` : ""}</div>
                    </div>
                    <div className="text-right text-sm">{idr(s.grand_total)} <div className="text-xs text-wmuted">{s.total_items} item</div></div>
                  </button>
                ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-taupe/5 rounded-lg px-3 py-2">
              <div className="text-sm"><b className="text-cocoa">{sale.number}</b>{sale.order_id ? ` · Order ${sale.order_id}` : ""} · {sale.customer_name || "-"}
                {sale.return_status ? <Badge variant="outline" className="ml-2 border-amber-300 text-amber-700">{sale.return_status === "Returned" ? "Sudah Return Penuh" : "Sebagian Return"}</Badge> : null}
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setSale(null); setLines({}); }} data-testid="ret-change-sale"><X className="w-4 h-4 mr-1" />Ganti Nota</Button>
            </div>

            <div className="border border-taupe/20 rounded-lg overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead className="text-center">Sisa</TableHead><TableHead className="w-24">Qty Return</TableHead><TableHead className="w-32">Kondisi</TableHead><TableHead className="w-56">Gudang Tujuan</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(sale.items || []).map((it) => {
                    const ln = lines[it.sale_item_id] || {};
                    const disabled = it.remaining <= 0;
                    return (
                      <TableRow key={it.sale_item_id} data-testid={`ret-item-${it.sale_item_id}`}>
                        <TableCell className="text-sm">{it.product_name} <span className="text-wmuted">{it.color} {it.size}</span><div className="text-xs text-wmuted">{idr(it.price)} · terjual {it.qty}</div></TableCell>
                        <TableCell className="text-center">{it.remaining}</TableCell>
                        <TableCell><Input type="number" min={0} max={it.remaining} value={ln.qty ?? 0} disabled={disabled} onChange={(e) => setLine(it.sale_item_id, { qty: Math.max(0, Math.min(it.remaining, Number(e.target.value))) })} className="h-8 w-20" data-testid={`ret-qty-${it.sale_item_id}`} /></TableCell>
                        <TableCell>
                          <Select value={ln.condition || "layak"} onValueChange={(v) => setLine(it.sale_item_id, { condition: v })} disabled={disabled}>
                            <SelectTrigger className="h-8" data-testid={`ret-cond-${it.sale_item_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="layak">Layak</SelectItem><SelectItem value="rusak">Rusak</SelectItem></SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select value={ln.warehouse_id || ""} onValueChange={(v) => setLine(it.sale_item_id, { warehouse_id: v })} disabled={disabled}>
                            <SelectTrigger className="h-8" data-testid={`ret-wh-${it.sale_item_id}`}><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
                            <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div><Label>Nilai Return</Label><Input value={idr(returnedValue)} disabled className="bg-taupe/5" data-testid="ret-value" /></div>
              <div><Label>Metode Refund</Label>
                <Select value={refundMethod} onValueChange={setRefundMethod}>
                  <SelectTrigger data-testid="ret-refund-method"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="Transfer">Transfer</SelectItem><SelectItem value="Store Credit">Store Credit</SelectItem><SelectItem value="Tanpa Refund">Tanpa Refund</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Jumlah Refund</Label><Input type="number" min={0} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="ret-refund-amount" /></div>
            </div>
            <div><Label>Catatan</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Alasan return (opsional)" data-testid="ret-note" /></div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          {sale && <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="ret-save-btn"><PackageCheck className="w-4 h-4 mr-1.5" />{saving ? "Menyimpan..." : "Proses Return"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ row, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ret-detail-dialog">
        <DialogHeader>
          <DialogTitle>Detail Return — {row.number}</DialogTitle>
          <DialogDescription>Nota {row.sale_number}{row.order_id ? ` · Order ${row.order_id}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-wmuted">Customer:</span> {row.customer_name || "-"}</div>
            <div><span className="text-wmuted">Total Qty:</span> {num(row.total_qty)}</div>
            <div><span className="text-wmuted">Nilai Return:</span> {idr(row.returned_value)}</div>
            <div><span className="text-wmuted">Refund:</span> {idr(row.refund_amount)} ({row.refund_method})</div>
          </div>
          {row.note && <div><span className="text-wmuted">Catatan:</span> {row.note}</div>}
          <div className="border border-taupe/20 rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead className="text-center">Qty</TableHead><TableHead className="text-center">Kondisi</TableHead><TableHead>Gudang</TableHead><TableHead className="text-right">Subtotal</TableHead></TableRow></TableHeader>
              <TableBody>
                {(row.items || []).map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="text-sm">{it.product_name} <span className="text-wmuted">{it.color} {it.size}</span></TableCell>
                    <TableCell className="text-center">{num(it.qty)}</TableCell>
                    <TableCell className="text-center"><Badge variant="outline" className={it.condition === "layak" ? "border-green-300 text-green-700" : "border-red-300 text-red-600"}>{it.condition === "layak" ? "Layak" : "Rusak"}</Badge></TableCell>
                    <TableCell className="text-sm">{it.warehouse_name}</TableCell>
                    <TableCell className="text-right">{idr(it.line_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-wmuted">Dibuat oleh {row.created_by} • {(row.created_at || "").slice(0, 16).replace("T", " ")}</div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
